import { Microphone } from "@phosphor-icons/react";
import clsx from "clsx";
import { type JSX, useState } from "react";

import type { VoiceTemplateSchemaType } from "features/templates/schemas";
import {
  RecordAudioButton,
  VoiceRecording,
} from "features/voice-message/components";

import { Button } from "common/components/ui/Button";
import useWorkspaceAttachmentBlob from "common/datahooks/useWorkspaceAttachmentBlob";
import useWorkspaceAttachmentsMutations from "common/datahooks/useWorkspaceAttachmentsMutations";
import useWorkspaceAttachmentUrl from "common/datahooks/useWorkspaceAttachmentUrl";

import type { UseFormReturn } from "react-hook-form";

export default function VoiceTemplate({
  form,
  isEditable,
  className,
}: {
  form: UseFormReturn<VoiceTemplateSchemaType>;
  isEditable: boolean;
  className?: string;
}) {
  const { getValues, setValue, setError, formState } = form;

  const attachment = getValues("attachments")?.[0];

  const { uploadAttachment, isUploadingAttachment } =
    useWorkspaceAttachmentsMutations();
  const { attachmentUrl, isLoadingAttachmentUrl } = useWorkspaceAttachmentUrl(
    attachment?.id,
  );
  const { attachmentBlob, isLoadingAttachmentBlob } =
    useWorkspaceAttachmentBlob(attachmentUrl);
  const [isRecording, setIsRecording] = useState(false);

  let mainComponent: JSX.Element;

  if (isRecording || attachment) {
    mainComponent = (
      <VoiceRecording
        initialBlob={attachmentBlob}
        isLoading={isLoadingAttachmentBlob || isLoadingAttachmentUrl}
        isUploading={isUploadingAttachment}
        onRecordingEnd={(audioBlob) => {
          if (audioBlob) {
            uploadAttachment({ file: audioBlob })
              .then((uploadedAttachment) => {
                setValue("attachments", [uploadedAttachment], {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setIsRecording(false);
              })
              .catch(() => {
                setError("attachments", {
                  message: "Error uploading attachment",
                });
              });
          } else {
            setIsRecording(false);
          }
        }}
        onRecordingDelete={
          isEditable
            ? () => {
                if (attachment) {
                  setValue("attachments", []);
                } else {
                  setIsRecording(false);
                }
              }
            : undefined
        }
      />
    );
  } else {
    mainComponent = (
      <RecordAudioButton onRecord={() => setIsRecording(true)}>
        {(buttonProps) => {
          return (
            <Button
              {...buttonProps}
              size="lg"
              leftIcon={<Microphone />}
              variant="default-purple"
              className="my-0.5"
            >
              Start recording
            </Button>
          );
        }}
      </RecordAudioButton>
    );
  }
  const hasError = "attachments" in formState.errors;

  return (
    <div className={clsx("flex flex-col", className)}>
      <div
        className={clsx(
          "flex flex-col border pt-3 px-2 pb-2 grow gap-y-3 rounded-3xl items-center",
          hasError ? "border-red-500" : "border-black-200",
        )}
      >
        {mainComponent}
        <span className="text-black-500 ml-auto text-xs mt-auto">
          Max: 1:00
        </span>
      </div>
      {hasError && (
        <span className="text-xs ml-1 mt-1 text-red-500">
          {formState.errors.attachments.message}
        </span>
      )}
    </div>
  );
}
